/**
 * How many accounts can still order and have it ship inside the month.
 *
 * The cutoff rule swept across the remaining days. It falls, never rises, and
 * the shape is the point: certainty arrives long before the month ends, because
 * a long-lead account stops being able to affect it weeks early.
 *
 * Counted over the ship-tos this report models, which is a wider population than
 * the agent's own. The panel subtitle says which, because the two differ and a
 * reader comparing this line to the agent's would otherwise assume a fault.
 */

import { useElementWidth } from '../shared/useElementWidth'

import { dayLabel } from './format'

export interface DecayPoint {
  readonly day: string
  readonly accounts: number
}

interface Props {
  readonly points: readonly DecayPoint[]
}

const HEIGHT = 240
const MARGIN = { top: 26, right: 18, bottom: 40, left: 46 }

export function DecayChart({ points }: Props) {
  const { ref, width } = useElementWidth<HTMLDivElement>()
  const chartWidth = Math.max(width || 420, 320)

  if (points.length < 2) {
    return <div className="state">Not enough days left in the month to plot</div>
  }

  const max = Math.max(...points.map((p) => p.accounts)) * 1.12
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom
  const plotWidth = chartWidth - MARGIN.left - MARGIN.right
  const y = (value: number) => MARGIN.top + plotHeight * (1 - value / max)
  const x = (index: number) => MARGIN.left + (index / (points.length - 1)) * plotWidth

  const line = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${String(x(index))} ${String(y(point.accounts))}`)
    .join(' ')
  const area = `${line} L${String(x(points.length - 1))} ${String(y(0))} L${String(x(0))} ${String(y(0))} Z`

  const ticks = [0, 0.5, 1].map((f) => Math.round(f * max))
  const first = points[0]
  const last = points[points.length - 1]

  return (
    <div className="mef-chart-wrap" ref={ref}>
      <svg
        viewBox={`0 0 ${String(chartWidth)} ${String(HEIGHT)}`}
        width={chartWidth}
        height={HEIGHT}
        role="img"
        aria-label={`Accounts still able to affect the month, falling from ${String(first?.accounts ?? 0)} to ${String(last?.accounts ?? 0)}.`}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={MARGIN.left}
              x2={chartWidth - MARGIN.right}
              y1={y(tick)}
              y2={y(tick)}
              className="mef-gridline"
            />
            <text x={MARGIN.left - 8} y={y(tick) + 3.5} className="mef-axis" textAnchor="end">
              {tick.toLocaleString()}
            </text>
          </g>
        ))}

        <path d={area} className="mef-decay-area" />
        <path d={line} className="mef-decay-line" />

        {points.map((point, index) => (
          <circle
            key={point.day}
            cx={x(index)}
            cy={y(point.accounts)}
            r={3}
            className="mef-decay-dot"
          >
            <title>{`${dayLabel(point.day)}: ${point.accounts.toLocaleString()} can still act`}</title>
          </circle>
        ))}

        {first !== undefined && (
          <text x={x(0)} y={y(first.accounts) - 11} className="mef-bar-value">
            {first.accounts.toLocaleString()}
          </text>
        )}
        {last !== undefined && (
          <text
            x={x(points.length - 1)}
            y={y(last.accounts) - 11}
            className="mef-bar-value"
            textAnchor="end"
          >
            {last.accounts.toLocaleString()}
          </text>
        )}

        <text x={MARGIN.left} y={HEIGHT - 10} className="mef-axis">
          {dayLabel(first?.day ?? null)}
        </text>
        <text x={chartWidth - MARGIN.right} y={HEIGHT - 10} className="mef-axis" textAnchor="end">
          {dayLabel(last?.day ?? null)}
        </text>

        <line
          x1={MARGIN.left}
          x2={chartWidth - MARGIN.right}
          y1={y(0)}
          y2={y(0)}
          className="mef-baseline"
        />
      </svg>
    </div>
  )
}
